// Training Platform ("TP") API Swagger:
//
// http://experiment-api.ml.motional.com/#/

import {
  isNonNullObject /*, isNullableString*/,
  isOptionalString,
  isString,
  isOptionalNumber,
  isNumber,
} from 'packages/lib/common-types/types';
import { swrFetcher } from 'packages/lib/utils/fetch';
import { isArrayOf } from 'packages/lib/common-types/types';
import { remove } from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import useSWR, { SWRResponse } from 'swr';
import { isArray } from 'lodash';
import { UnexpectedResponseType } from 'packages/lib/utils/error';

const testShowAllJobs = false; // For testing only

export enum TPStatus {
  Building = 'Building',
  Pending = 'Pending',
  Aborting = 'Aborting',
  Aborted = 'Aborted',
  Running = 'Running',
  Restarting = 'Restarting',
  Completing = 'Completing',
  Completed = 'Completed',
  Terminating = 'Terminating',
  Terminated = 'Terminated',
  Failed = 'Failed',
  Unknown = 'Unknown',
}

export function isJobCancelable(job: TPJob): boolean {
  return !!(
    job.status === TPStatus.Building ||
    job.status === TPStatus.Pending ||
    job.status === TPStatus.Running ||
    job.status === TPStatus.Restarting ||
    job.status === TPStatus.Completing ||
    job.status === TPStatus.Unknown
  );
}

// Per Gordon Gustafson: Usually folks aren't interested in completed or failed jobs that aren't theirs.
export function showTPJobStatusOnAllPage(status: TPStatus): boolean {
  return !!(
    status === TPStatus.Building ||
    status === TPStatus.Running ||
    status === TPStatus.Pending ||
    status === TPStatus.Restarting ||
    testShowAllJobs
  );
}

export interface TPJob {
  costPerHr: number; // 5.26
  costSoFar?: number; // 63.432682
  created: string; // '2021-12-01T20:20:04.638347Z'
  department: string; // "autonomy - platform"
  instanceType?: string; // "g4dn.8xlarge"
  jobName: string; // 'isn-train-builderhrjmwbbcwd'
  manager: string; // "yiluan guo"
  owner: string; // "vern jensen" (first and last name from OAuth token)
  status: TPStatus;
  trainingMetrics: string; // "s/it: 31.42, s/sample: 42.35"
  workers?: string; // "2"
}
export interface TPJobResponse {
  result: TPJob[];
}

export const isTPJob = (job: unknown): job is TPJob => {
  const isValid =
    isNonNullObject(job) &&
    isString(job.jobName) &&
    isString(job.status) &&
    isString(job.created) &&
    isString(job.owner) &&
    isString(job.department) &&
    isString(job.manager) &&
    isOptionalString(job.workers) &&
    isNumber(job.costPerHr) &&
    isOptionalString(job.trainingMetrics) &&
    isOptionalNumber(job.costSoFar);
  if (!isValid) console.log('isTPJob failed for job: ', job);
  return isValid;
};

export const isTPJobResponse = (resp: unknown): resp is TPJobResponse =>
  isNonNullObject(resp) && isArrayOf(isTPJob)(resp.result);

/** @username - pass 'all' OR the user's name as parsed from their email address (convert dot to space), for consistency's sake. (APF-8858) */
export const useTPJobsSWR = (
  username = 'all',
  showActiveJobsOnly = true,
  useNewCluster: boolean
): SWRResponse<TPJobResponse, UnexpectedResponseType> => {
  const searchParams = new URLSearchParams({ owner: username });
  if (showActiveJobsOnly) {
    searchParams.append('statusFilter', 'Building');
    searchParams.append('statusFilter', 'Running');
    searchParams.append('statusFilter', 'Pending');
    searchParams.append('statusFilter', 'Restarting');
  }
  return useSWR<TPJobResponse>(
    username.trim().length > 0 ? `/job/list?${searchParams}` : null,
    (url: string) =>
      swrFetcher<TPJobResponse>(url, {
        baseURL: useNewCluster ? 'trainingPlatformEx' : 'trainingPlatformOld',
        typeGuard: isTPJobResponse,
      }),
    {
      revalidateOnFocus: true,
      suspense: false,
      refreshInterval: 45 * 1000,
      refreshWhenHidden: false,
      focusThrottleInterval: 10 * 1000,
    }
  );
};

export interface CancelJobResponse {
  succeeded?: boolean;
  // The following is present only if succeeded is false:
  code?: number;
  message?: string; // Error message
  details?: Array<{ typeUrl: string; value: string }>;
}

export const isCancelJobResponse = (resp: unknown): resp is CancelJobResponse =>
  isNonNullObject(resp) &&
  (typeof resp.succeeded === 'boolean' || typeof resp.message === 'string');

export const cancelJob = async (
  jobName: string,
  useNewCluster: boolean
): Promise<CancelJobResponse> => {
  const [data, resp] = await remove(
    useNewCluster ? 'trainingPlatformEx' : 'trainingPlatformOld',
    `/job?${new URLSearchParams({ jobName })}`
  );
  if (!isCancelJobResponse(data)) throw new UnexpectedResponseType(resp);
  return data;
};

interface InfoJob {
  name: string;
  image?: string; // "480982559644.dkr.ecr.us-east-1.amazonaws.com/ml-train-prod-nudeep:builderdckyuvkmtl"
  gitInfo?: string;
  owner?: string;
  email?: string;
  workers?: string; // "1"
  instanceType?: string;
  args?: string[];
  created?: string;
  updated?: string;
}

export interface TPJobInfoResponse {
  job?: InfoJob;
  status?: TPStatus;
  reason?: string;
  logLink?: string; // URL
  durationInMinutes?: string;
  jobDashboard?: string; // URL
}

// Currently unused
export const isInfoJob = (resp: unknown): resp is TPJobInfoResponse =>
  isNonNullObject(resp) &&
  typeof resp.name === 'string' &&
  isOptionalString(resp.image) &&
  isOptionalString(resp.gitInfo) &&
  isOptionalString(resp.owner) &&
  isOptionalString(resp.email) &&
  isOptionalString(resp.workers) &&
  isOptionalString(resp.instanceType) &&
  (typeof resp.args === 'undefined' ||
    (isArray(resp.args) &&
      resp.args.every((val) => typeof val === 'string'))) &&
  isOptionalString(resp.created) &&
  isOptionalString(resp.updated);

export const isTPJobInfoResponse = (resp: unknown): resp is TPJobInfoResponse =>
  isNonNullObject(resp) &&
  (typeof resp.job === 'undefined' || isInfoJob(resp.job)) &&
  isOptionalString(resp.status) &&
  isOptionalString(resp.reason) &&
  isOptionalString(resp.logLink) &&
  isOptionalString(resp.durationInMinutes) &&
  isOptionalString(resp.jobDashboard);

/** NOTE: the typeguard for this endpoint is minimal, as we use the <JSONAsTable> component to display the data,
    which can take ANY data. */
export const useTPJobInfoSWR = (
  jobName: string | null | undefined,
  useNewCluster: boolean
): SWRResponse<TPJobInfoResponse, UnexpectedResponseType> =>
  useSWR<TPJobInfoResponse>(
    jobName && jobName.length > 0
      ? `/job/status?${new URLSearchParams({ jobName })}`
      : null,
    (url: string) =>
      swrFetcher<TPJobInfoResponse>(url, {
        baseURL: useNewCluster ? 'trainingPlatformEx' : 'trainingPlatformOld',
        typeGuard: isNonNullObject, // Use isTPJobInfoResponse for a stricter typeguard
      }),
    { revalidateOnFocus: false, suspense: false }
  );
